import React, { useContext, useEffect, useState } from "react";
import { Drawer, Button, Typography, Row, Col, Empty, Divider, Skeleton } from "antd";
import { CartContext } from "../CartContext";
import { useNavigate } from "react-router-dom";
import { IconTrash, IconPlus, IconMinus, IconTruck, IconClock, IconX } from "@tabler/icons-react";
import AuthServ from "../service/AuthServ";
import ProductServ from "../service/ProductServ";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import SafeImage from "./SafeImage";
import RelatedProducts from "./RelatedProducts";
import CartRemoveConfirmModal from "./CartRemoveConfirmModal";
import { formatUnit, renderSafeText, formatDeliveryTime, formatIndianCurrency } from "../util/unitFormatter";
import "../css/cartDrawer.css";

const { Text, Title } = Typography;

const CartDrawer = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { cart, cartCount, removeFromCart, updateQuantity, loading, addToCart } = useContext(CartContext);
    const { showAlert } = useGlobalAuthModal();
    const [localLoading, setLocalLoading] = useState(false);
    const [shopRelatedMap, setShopRelatedMap] = useState({});
    const [relatedLoadingMap, setRelatedLoadingMap] = useState({});

    // Deletion Modal State
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);

    const cartItems = cart?.items || [];
    const billDetails = cart?.billDetails || {};

    // Fetch Related Products (Shop-wise)
    useEffect(() => {
        const fetchAllRelated = async () => {
            if (cartItems.length > 0) {
                // 1. Group items by shop
                const shops = {};
                cartItems.forEach(item => {
                    const sId = (item.product?.shopId?._id || item.product?.shopId || item.shopId || "unknown").toString();
                    if (!shops[sId]) shops[sId] = [];
                    shops[sId].push(item);
                });

                const newRelatedMap = { ...shopRelatedMap };
                const newLoadingMap = { ...relatedLoadingMap };

                // 2. Fetch for each shop
                const shopIds = Object.keys(shops);
                const fetchPromises = shopIds.map(async (sId) => {
                    // Skip if already loading or already have data (optional optimization)
                    // For now, refetch to keep it dynamic as per requirement
                    const shopItems = shops[sId];
                    const baseProduct = shopItems[shopItems.length - 1].product;
                    const baseProductId = baseProduct?._id || baseProduct;

                    if (!baseProductId) return;

                    newLoadingMap[sId] = true;
                    setRelatedLoadingMap({ ...newLoadingMap });

                    try {
                        const data = await ProductServ.getRelatedProducts(baseProductId, { shopId: sId });
                        const cartProductIds = cartItems.map(item => (item.product?._id || item.product).toString());
                        const filtered = (data?.data || data || []).filter(p => !cartProductIds.includes(p._id.toString()));
                        newRelatedMap[sId] = filtered;
                    } catch (error) {
                        console.error(`Error fetching related products for shop ${sId}:`, error);
                    } finally {
                        newLoadingMap[sId] = false;
                        setRelatedLoadingMap({ ...newLoadingMap });
                    }
                });

                await Promise.all(fetchPromises);
                setShopRelatedMap(newRelatedMap);
            } else {
                setShopRelatedMap({});
            }
        };

        if (isOpen) {
            fetchAllRelated();
        }
    }, [cartItems.length, isOpen]);

    const handleRelatedAddToCart = async (product) => {
        const options = {};
        if (product.options && product.options.length > 0) {
            // Pick first variant of first option by default in drawer for speed
            const firstOpt = product.options[0];
            if (firstOpt.variants && firstOpt.variants.length > 0) {
                options[firstOpt.name] = firstOpt.variants[0].val || firstOpt.variants[0].value;
            }
        }
        await addToCart(product._id, 1, options, {
            name: product.name,
            image: product.imageUrl || (product.images && product.images[0]),
            variant: product.variantType ? (options[Object.keys(options)[0]]) : null
        });
    };

    // Handle Checkout
    const handleCheckout = () => {
        if (!AuthServ.isLogged()) {
            onClose();
            showAlert({
                title: "Authentication Required",
                description: "Please log in to proceed to checkout.",
                buttons: [
                    { text: "Cancel", variant: "secondary" },
                    { text: "Login", variant: "primary", onClick: () => window.location.href = "/customer-login?redirect=/checkout" }
                ]
            });
            return;
        }
        onClose();
        navigate("/checkout"); // Or handle address selection drawer here if preferred
    };

    const triggerRemoveConfirm = (itemId) => {
        setItemToRemove(itemId);
        setRemoveModalVisible(true);
    };

    const handleConfirmRemove = async () => {
        if (itemToRemove) {
            await removeFromCart(itemToRemove);
            setRemoveModalVisible(false);
            setItemToRemove(null);
        }
    };

    return (
        <Drawer
            title={
                <div className="cart-drawer-header">
                    <span className="cart-drawer-title">My Cart ({cartCount} Items)</span>
                </div>
            }
            placement="right"
            onClose={onClose}
            open={isOpen}
            width={window.innerWidth < 576 ? '92%' : 450}
            className="cart-drawer-custom"
            closeIcon={<IconX size={20} />}
            footer={
                cartItems.length > 0 && (
                    <div className="cart-drawer-footer">
                        {cart?.shopAvailability && !cart.shopAvailability.isOpen ? (
                            <div style={{ width: '100%' }}>
                                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', textAlign: 'center', marginBottom: '8px' }}>
                                    🚨 {cart.shopAvailability.reopenMessage}
                                </div>
                                <Button
                                    type="primary"
                                    size="large"
                                    className="cart-checkout-btn"
                                    disabled
                                    style={{ width: '100%', opacity: 0.5 }}
                                >
                                    Checkout Unavailable
                                </Button>
                            </div>
                        ) : (
                            <div className="cart-footer-total-row">
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>₹{formatIndianCurrency(billDetails.toPay || 0)}</Title>
                                    <span style={{ fontSize: 10, color: '#9ca3af' }}>Exact total confirmed at checkout</span>
                                </div>
                                <Button
                                    type="primary"
                                    size="large"
                                    className="cart-checkout-btn"
                                    onClick={handleCheckout}
                                    loading={loading}
                                >
                                    Proceed to Payment
                                </Button>
                            </div>
                        )}
                    </div>
                )
            }
        >
            {loading && !cartItems.length ? (
                <Skeleton active paragraph={{ rows: 4 }} />
            ) : cartItems.length > 0 ? (
                <div className="cart-drawer-content">
                    {/* 1. Delivery Time Banner */}
                    {billDetails.deliveryTime && (
                        <div className="delivery-time-banner">
                            <div className="time-icon-wrapper">
                                <IconClock size={16} color="#363bd0" />
                            </div>
                            <div>
                                <Text strong>Delivery By {formatDeliveryTime(billDetails.deliveryTime)}</Text>
                                <div className="text-sub-description">Shipment of {cartItems.length} items</div>
                            </div>
                        </div>
                    )}

                    {/* 2. Items List */}
                    <div className="cart-items-list">
                        {cartItems.map((item) => (
                            <div key={item._id} className="cart-drawer-item">
                                <div className="cart-item-img-wrapper pcu-cursor-pointer" onClick={() => window.open(`/single/Products/${item.product?._id || item.product}`, '_blank')}>
                                    <SafeImage
                                        src={item.product?.imageUrl || (item.product?.images && item.product.images[0])}
                                        alt={item.product?.name}
                                        variant="thumbnail"
                                        className="cart-item-img"
                                    />
                                </div>
                                <div className="cart-item-info">
                                    <Text className="cart-item-name pcu-cursor-pointer" onClick={() => window.open(`/single/Products/${item.product?._id || item.product}`, '_blank')}>{renderSafeText(item.product?.name)}</Text>
                                    <div className="cart-item-meta">
                                        {item.selectedOptions && Object.entries(item.selectedOptions).map(([k, v]) => (
                                            <span key={k}>{formatUnit(v, k)}</span>
                                        ))}
                                    </div>

                                    <div className="cart-item-price-row">
                                        <div className="cart-price-block">
                                            {/* We use resolved price from backend or component logic? 
                                       Ideally backend sends resolved price, but for now we rely on simple display logic 
                                       since bill details handles the grand total. 
                                   */}
                                            <Text strong className="cart-item-current-price">
                                                ₹{formatIndianCurrency(item.price || item.product?.price)}
                                            </Text>
                                            {(item.mrp || item.product?.mrp) > (item.price || item.product?.price) && (
                                                <Text delete className="cart-mrp">MRP ₹{formatIndianCurrency(item.mrp || item.product.mrp)}</Text>
                                            )}
                                        </div>

                                        <div className="cart-item-actions-drawer">
                                            <div className="cart-qty-control">
                                                <Button
                                                    type="text"
                                                    icon={<IconMinus size={14} />}
                                                    size="small"
                                                    onClick={() => item.quantity > 1 ? updateQuantity(item._id, item.quantity - 1) : triggerRemoveConfirm(item._id)}
                                                />
                                                <span className="qty-value">{item.quantity}</span>
                                                <Button
                                                    type="text"
                                                    icon={<IconPlus size={14} />}
                                                    size="small"
                                                    onClick={() => updateQuantity(item._id, item.quantity + 1)}
                                                />
                                            </div>
                                            <Button
                                                type="text"
                                                icon={<IconTrash size={18} />}
                                                className="cart-item-remove-btn-drawer"
                                                onClick={() => triggerRemoveConfirm(item._id)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Shop-wise Related Products */}


                    {/* 3. Bill Details */}
                    <div className="bill-details-section">
                        <Title level={5} className="bill-section-title">Order Summary</Title>

                        <div className="bill-row">
                            <Text>Subtotal</Text>
                            <Text>₹{formatIndianCurrency(billDetails.itemTotal)}</Text>
                        </div>



                        {billDetails.totalDiscount > 0 && (
                            <div className="bill-row success-text">
                                <Text type="success">Discount</Text>
                                <Text type="success">- ₹{formatIndianCurrency(billDetails.totalDiscount)}</Text>
                            </div>
                        )}

                        {billDetails.platformFee > 0 && (
                            <div className="bill-row">
                                <Text>Platform Fee</Text>
                                <Text>₹{formatIndianCurrency(billDetails.platformFee)}</Text>
                            </div>
                        )}

                        {/* Delivery Fee — displayed as estimate; final fee is calculated at checkout */}
                        <div className="bill-row">
                            <div className="flex-row-center" style={{ gap: 4 }}>
                                {/* <IconTruck size={14} style={{ marginRight: 2, color: '#f97316' }} /> */}
                                <Text>Estimated Delivery Fee</Text>
                            </div>
                            {billDetails.deliveryFee > 0 ? (
                                <Text style={{ color: '#f97316' }}>₹{formatIndianCurrency(billDetails.deliveryFee)}</Text>
                            ) : (
                                <Text type="success">FREE</Text>
                            )}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', paddingBottom: 10, marginTop: -10 }}>
                            Final delivery fee will be calculated at checkout.
                        </div>

                        <div className="bill-row">
                            <Text>Handling Fee</Text>
                            {billDetails.handlingFee > 0 ? (
                                <Text>₹{formatIndianCurrency(billDetails.handlingFee)}</Text>
                            ) : (
                                <Text type="success">FREE</Text>
                            )}
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        <div className="bill-row grand-total">
                            <Text strong>Estimated Total</Text>
                            <Text strong>₹{formatIndianCurrency(billDetails.grandTotal)}</Text>
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: -4, marginBottom: 4 }}>
                            Exact total confirmed at checkout
                        </div>
                        {billDetails.savings > 0 && (
                            <div className="cart-savings-strip">
                                Yay! You saved ₹{formatIndianCurrency(billDetails.savings)} on this order
                            </div>
                        )}
                    </div>

                    <div className="cancellation-policy-text">
                        Review your order and address details to avoid cancellations.
                    </div>

                    {Object.entries(shopRelatedMap).map(([shopId, products]) => (
                        <RelatedProducts
                            key={shopId}
                            shopId={shopId}
                            products={products}
                            loading={relatedLoadingMap[shopId]}
                            onAddToCart={handleRelatedAddToCart}
                            title={products.length > 0 ? `More from ${products[0].shopId?.name || 'this shop'}` : 'You May Also Like'}
                        />
                    ))}

                </div>
            ) : (
                <div className="cart-empty-state">
                    <div style={{
                        width: 90, height: 90, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 20px', boxShadow: '0 4px 16px rgba(249,115,22,0.15)'
                    }}>
                        <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                            stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                            <line x1="3" y1="6" x2="21" y2="6" />
                            <path d="M16 10a4 4 0 0 1-8 0" />
                        </svg>
                    </div>
                    <Title level={4} style={{ margin: 0 }}>Your cart is empty</Title>
                    <Text type="secondary">Browse products and add items to your cart.</Text>
                    <Button type="primary" onClick={() => { onClose(); navigate("/all-products"); }} style={{ marginTop: 16 }}>
                        Start Shopping
                    </Button>
                </div>
            )}
            <CartRemoveConfirmModal
                visible={removeModalVisible}
                onCancel={() => setRemoveModalVisible(false)}
                onConfirm={handleConfirmRemove}
                loading={loading}
            />
        </Drawer>
    );
};

export default CartDrawer;
